import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { projectsData } from '../i18n/translations';
import { MapPin } from 'lucide-react';
import SEO from '../components/SEO';

const Projects = () => {
  const { language, t } = useLanguage();
  const [filter, setFilter] = useState('all');

  const seoData = {
    es: {
      title: 'Promociones Inmobiliarias | PASTORIA SERRALTA - Viviendas en Mallorca',
      description: 'Descubre nuestras promociones inmobiliarias en Palma de Mallorca. Viviendas de calidad en curso y terminadas. SERRALTA RESIDENCIAL, LLAC COMO, SON DAMETO y más.'
    },
    en: {
      title: 'Real Estate Projects | PASTORIA SERRALTA - Homes in Mallorca',
      description: 'Discover our real estate developments in Palma de Mallorca. Quality homes in progress and completed. SERRALTA RESIDENCIAL, LLAC COMO, SON DAMETO and more.'
    }
  };

  const filteredProjects = projectsData.filter(project => {
    if (filter === 'all') return true;
    return project.status === filter;
  });

  const filters = [
    { value: 'all', label: t.projects.filter.all },
    { value: 'inProgress', label: t.projects.filter.inProgress },
    { value: 'completed', label: t.projects.filter.completed }
  ];

  return (
    <div className="min-h-screen" data-testid="projects-page">
      <SEO 
        title={seoData[language].title}
        description={seoData[language].description}
        url={language === 'es' ? '/promociones' : '/en/projects'}
      />
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/image31.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#356C8A]/80" />
        </div>
        
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl sm:text-6xl font-bold" data-testid="projects-title">
            {t.projects.title}
          </h1>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filterOption) => (
              <button
                key={filterOption.value}
                onClick={() => setFilter(filterOption.value)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  filter === filterOption.value
                    ? 'bg-[#356C8A] text-white'
                    : 'bg-[#E8F2F7] text-gray-700 hover:bg-[#C5DEE8]'
                }`}
                data-testid={`filter-button-${filterOption.value}`}
              >
                {filterOption.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-[#E8F2F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={`${language === 'es' ? '/promociones' : '/en/projects'}/${project.slug}`}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all transform hover:-translate-y-1"
                data-testid={`project-card-${project.slug}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`/${project.images[0]}`}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#356C8A] text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                    {project.soldPercentage}% {t.projects.status.sold}
                  </div>
                  {project.status === 'completed' && (
                    <div className="absolute bottom-4 left-4 bg-[#356C8A] text-white px-4 py-2 rounded-full font-semibold text-sm">
                      {t.projects.status.completed}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#356C8A] mb-3">
                    {project.name}
                  </h3>
                  <div className="flex items-center text-gray-600 text-sm mb-4">
                    <MapPin className="h-4 w-4 mr-2 text-[#356C8A]" />
                    <span>{project.location}</span>
                  </div>
                  <p className="text-gray-700 line-clamp-3">
                    {project.description[language]}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;